/* Print-only route — bare canvas, no app shell.
   Renders ResumeRenderer at exact CONTENT dimensions (page - 2×margin)
   so multi-page resumes get EQUAL 4-side padding on every page via
   Chromium's @page margin rule.
*/
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ResumeRenderer from "@/components/ResumeRenderer";

const PAGE_SIZES_MM = {
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
  Legal: { w: 215.9, h: 355.6 },
};

export default function Print() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [style, setStyle] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    if (token) localStorage.setItem("rf_token", token);
    (async () => {
      try {
        const r = await api.get(`/resumes/${id}`);
        setData(r.data.data);
        const tpls = await api.get("/templates");
        const tpl = tpls.data.find((t) => t.id === r.data.template_id) || tpls.data.find((t) => t.is_default) || null;
        setStyle(tpl);
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
        await new Promise((res) => setTimeout(res, 300));
        setReady(true);
      } catch (e) {
        console.error("print fetch failed", e);
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  if (!data || !style) return null;

  const size = style.page_size === "Custom"
    ? { w: Number(style.custom_width_mm) || 210, h: Number(style.custom_height_mm) || 297 }
    : PAGE_SIZES_MM[style.page_size] || PAGE_SIZES_MM.A4;

  const marginMm = Math.max(0, Number(style.page_margin_mm ?? 12));
  // Hero templates can bleed to the top edge of page 1 for the banner;
  // all other pages still get the equal margin.
  const heroEdgeToEdge = !!style.hero_enabled;

  return (
    <>
      <style>{`
        @page {
          size: ${size.w}mm ${size.h}mm;
          margin: ${marginMm}mm;
        }
        @page :first {
          margin-top: ${heroEdgeToEdge ? 0 : marginMm}mm;
          margin-left: ${heroEdgeToEdge ? 0 : marginMm}mm;
          margin-right: ${heroEdgeToEdge ? 0 : marginMm}mm;
        }
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #f5f6f8 !important;
        }
        body * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        @media print {
          html, body { background: white !important; }
          #print-canvas { box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
        }
        @media screen {
          body {
            padding: ${marginMm}mm;
            display: flex;
            justify-content: center;
          }
          #print-canvas {
            box-shadow: 0 12px 40px -8px rgba(15, 23, 42, 0.18);
          }
        }
        /* The paper renders at (page - 2*margin); just ensure no extra box */
        #print-canvas .paper {
          box-shadow: none !important;
          border-radius: 0 !important;
          border: none !important;
        }
        [data-page-break] {
          page-break-after: always !important;
          break-after: page !important;
          height: 0 !important;
        }
      `}</style>
      <div id="print-canvas" data-print-ready={ready ? "true" : "false"} data-testid="print-canvas">
        <ResumeRenderer data={data} style={style} />
      </div>
    </>
  );
}
